import { ReceiptText } from 'components/icons/customIcons';
import { BarChart3, Calculator, ClipboardCheck, FileText, NotebookPen, Paperclip, UserPlus, Users } from 'lucide-react';
import React from 'react';
import { useLocation, Link, matchPath } from 'react-router-dom';

// Interface pour définir une route du breadcrumb
interface BreadcrumbRoute {
  path: string;
  label: string;
  icon?: React.ReactNode;
}

// Configuration des routes pour le breadcrumb
interface BreadcrumbConfig {
  [key: string]: BreadcrumbRoute;
}

// Props du composant Breadcrumb
interface BreadcrumbProps {
  config: BreadcrumbConfig;
  separator?: React.ReactNode;
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  config,
  separator = '/',
  className = ''
}) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Construire les éléments du breadcrumb
  const breadcrumbItems: BreadcrumbRoute[] = [];

  // Toujours ajouter l'accueil
  if (config['/']) {
    breadcrumbItems.push(config['/']);
  }

  // Dynamic entries (keys with a `:param` segment, e.g. '/validations/:demandeId')
  const dynamicKeys = Object.keys(config).filter((key) => key.includes(':'));

  // Construire le chemin progressivement
  let currentPath = '';
  pathSegments.forEach((segment) => {
    currentPath += `/${segment}`;
    if (config[currentPath]) {
      breadcrumbItems.push(config[currentPath]);
      return;
    }
    const dynamicKey = dynamicKeys.find((key) => matchPath({ path: key, end: true }, currentPath));
    if (dynamicKey) {
      // The crumb links to the concrete path, not to the pattern
      breadcrumbItems.push({ ...config[dynamicKey], path: currentPath });
    }
  });

  if (breadcrumbItems.length <= 1) {
    return null; // Ne rien afficher si on est sur la page d'accueil
  }

  return (
    <nav className={`flex mb-6 items-center space-x-2 text-sm text-gray-600 ${className}`}>
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;

        return (
          <React.Fragment key={item.path}>
            {index > 0 && (
              <span className="text-zinc-400 mx-2">{separator}</span>
            )}
            {isLast ? (
              <span className="text-zinc-900 font-medium flex items-center gap-1">
                {item.icon}
                {item.label}
              </span>
            ) : (
              <Link
                to={item.path}
                className="hover:text-primary transition-colors flex items-center gap-1"
              >
                {item.icon}
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export const breadcrumbConfig: BreadcrumbConfig = {
  '/': {
    path: '/',
    label: 'Accueil',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    )
  },
  '/mes-documents': {
    path: '/mes-documents',
    label: 'Mes documents',
    icon: (
    <Paperclip className='w-4 h-4'/>
    )
  },
  '/mes-simulations': {
    path: '/mes-simulations',
    label: 'Mes simulations',
    icon: (
    <Calculator className='w-4 h-4'/>
    )
  },
  '/mes-devis': {
    path: '/mes-devis',
    label: 'Mes devis',
    icon: (
    <ReceiptText className='w-4 h-4'/>
    )
  },
  '/mes-statistiques': {
    path: '/mes-statistiques',
    label: 'Mes Statistiques',
    icon: (
    <BarChart3 className='w-4 h-4'/>
    )
  },
  '/mes-contrats': {
    path: '/mes-contrats',
    label: 'Mes contrats',
    icon: (
    <FileText className='w-4 h-4'/>
    )
  },
  '/mes-contrats-collectifs': {
    path: '/mes-contrats-collectifs',
    label: 'Mes contrats collectifs',
    icon: (
    <Users className='w-4 h-4'/>
    )
  },
  '/mes-clients': {
    path: '/mes-clients',
    label: 'Mes clients',
    icon: (
    <Users className='w-4 h-4'/>
    )
  },

  '/mes-notes': {
    path: '/mes-notes',
    label: 'Mes notes',
    icon: (
    <NotebookPen className='w-4 h-4'/>
    )
  },

  '/mes-leads': {
    path: '/mes-leads',
    label: 'Mes Leads',
    icon: (
    <UserPlus className='w-4 h-4'/>
    )
  },
  '/validations': {
    path: '/validations',
    label: 'Validations',
    icon: (
    <ClipboardCheck className='w-4 h-4'/>
    )
  },
  '/validations/:demandeId': {
    path: '/validations/:demandeId',
    label: 'Demande de validation',
    icon: (
    <ClipboardCheck className='w-4 h-4'/>
    )
  },
};
